import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import {
  SITE,
  SERVICES,
  CLEANING,
  REASONS,
  STEPS,
  FAQS,
  COMPARE_WIDE,
  HERO_SLIDES,
} from "../data/site";
import Seo, { localBusinessJsonLd } from "../Components/Seo";
import Picture from "../Components/Picture";
import Reveal from "../Components/Reveal";
import BeforeAfter from "../Components/BeforeAfter";

export default function Home() {
  const heroRef = useRef(null);
  const compareServices = SERVICES.filter((s) => s.compare);
  const [active, setActive] = useState(compareServices[0].slug);
  const [slide, setSlide] = useState(0);
  const current = compareServices.find((s) => s.slug === active) || compareServices[0];

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;
    const ctx = gsap.context(() => {
      gsap.from(".hero-anim", {
        y: 36,
        opacity: 0,
        duration: 1.05,
        stagger: 0.1,
        ease: "power3.out",
        delay: 0.08,
      });
    }, heroRef);
    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced || HERO_SLIDES.length < 2) return;
    let id;
    const start = () => {
      id = window.setInterval(() => {
        setSlide((s) => (s + 1) % HERO_SLIDES.length);
      }, 9000);
    };
    const stop = () => window.clearInterval(id);
    const onVis = () => {
      stop();
      if (!document.hidden) start();
    };
    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  const jsonLd = [
    localBusinessJsonLd(),
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: { "@type": "Answer", text: item.a },
      })),
    },
  ];

  return (
    <>
      <Seo
        title={`${SITE.name} – ${SITE.tagline} | Mülheim / Essen`}
        description="CCS Gebäudemanagement in Mülheim an der Ruhr: Abbruch, Entrümpelung, Umzug, Renovierung, Trockenbau, Fliesen und Gebäudereinigung – Festpreis, auch kurzfristig."
        path="/"
        jsonLd={jsonLd}
      />

      <section className="hero" ref={heroRef}>
        <div className="hero-media">
          {HERO_SLIDES.map((img, i) => (
            <div
              key={img.src}
              className={`hero-slide${i === slide ? " is-on" : ""}`}
              aria-hidden={i !== slide}
            >
              <Picture
                src={img.src}
                fallback={img.fallback}
                alt={i === slide ? img.alt : ""}
                width={976}
                height={940}
                loading={i === 0 ? "eager" : "lazy"}
                fetchPriority={i === 0 ? "high" : undefined}
              />
            </div>
          ))}
        </div>
        <div className="hero-content">
          <p className="kicker hero-anim" style={{ color: "var(--cyan)" }}>
            Mülheim · Essen · Ruhrgebiet
          </p>
          <h1 className="display hero-anim">
            Wir schaffen
            <br />
            <em>Räume.</em>
          </h1>
          <p className="hero-lead hero-anim">
            Abbruch, Ausbau, Umzug und Reinigung aus einer Hand. Festpreis, Fachpersonal,
            kurze Wege – damit aus einer Baustelle wieder Alltag wird.
          </p>
          <div className="hero-actions hero-anim">
            <Link className="btn btn-primary" to="/kontakt">
              Kostenloses Angebot
            </Link>
            <a className="btn btn-ghost" href={`tel:${SITE.phoneTel}`}>
              {SITE.phoneDisplay}
            </a>
          </div>
          <div className="hero-meta hero-anim">
            <div>
              <strong>Festpreis</strong>
              ohne versteckte Nachträge
            </div>
            <div>
              <strong>Kurzfristig</strong>
              Termine auch zeitnah
            </div>
            <div>
              <strong>Vor Ort</strong>
              {SITE.addresses[0].city}
            </div>
          </div>
        </div>
        <p className="hero-caption">{HERO_SLIDES[slide].caption}</p>
      </section>

      <section className="section intro">
        <div className="wrap intro-grid">
          <Reveal className="intro-copy">
            <p className="kicker">Das Unternehmen</p>
            <h2 className="display">Aus beengten Grundrissen werden offene Räume.</h2>
            <p className="intro-lead">
              CCS Gebäudemanagement begleitet Wohnungen, Häuser und Gewerbe in
              Mülheim, Essen und Umgebung – vom ersten Durchbruch bis zur
              besenreinen Übergabe. Ein Ansprechpartner, ein Terminplan, ein Preis.
            </p>
          </Reveal>
          <Reveal delay={120} className="intro-visual">
            <Picture
              src="/images/opt/start-image-3.webp"
              fallback="/images/start-image-3.jpg"
              alt="Renovierung: vom Rohzustand zum fertigen Wohnraum"
              width={976}
              height={1002}
            />
            <p className="intro-caption">Rohzustand — Wohnraum</p>
          </Reveal>
          <Reveal delay={80} className="intro-aside">
            <ul className="intro-points">
              <li>
                <span>01</span>
                <div>
                  <strong>Ein Ansprechpartner</strong>
                  <p>Vom Durchbruch bis zur Übergabe – ohne Schnittstellen.</p>
                </div>
              </li>
              <li>
                <span>02</span>
                <div>
                  <strong>Ein Terminplan</strong>
                  <p>Gewerke, die ineinandergreifen. Kurze Wege im Ruhrgebiet.</p>
                </div>
              </li>
              <li>
                <span>03</span>
                <div>
                  <strong>Ein Festpreis</strong>
                  <p>Was wir anbieten, rechnen wir so ab. Ohne Nachträge.</p>
                </div>
              </li>
            </ul>
            <Link className="intro-more" to="/#leistungen">
              Zu den Leistungen →
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="section" id="leistungen">
        <div className="wrap">
          <Reveal className="services-head">
            <p className="kicker">Leistungen</p>
            <h2 className="display">Alles, was Räume verändert.</h2>
          </Reveal>
          <div className="service-list">
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/leistungen/${s.slug}`} className="service-row">
                <span className="service-num">{s.number}</span>
                <span>
                  <span className="service-title">{s.title}</span>
                  <span className="service-short">{s.short}</span>
                </span>
                <span className="service-go">Ansehen →</span>
              </Link>
            ))}
            <Link to="/reinigung" className="service-row">
              <span className="service-num">{CLEANING.number}</span>
              <span>
                <span className="service-title">{CLEANING.title}</span>
                <span className="service-short">{CLEANING.short}</span>
              </span>
              <span className="service-go">Ansehen →</span>
            </Link>
          </div>
        </div>
      </section>

      <section className="section ba-section">
        <div className="wrap">
          <Reveal>
            <p className="kicker">Vorher / Nachher</p>
            <h2 className="display">Der Unterschied, den man sieht.</h2>
          </Reveal>
          <BeforeAfter
            key={current.slug}
            before={current.before}
            after={current.after}
            wide={COMPARE_WIDE}
            alt={`${current.title}: Vorher und Nachher`}
          />
          <div className="ba-switch" role="tablist" aria-label="Projekt wählen">
            {compareServices.map((s) => (
              <button
                key={s.slug}
                type="button"
                className={s.slug === active ? "is-on" : ""}
                onClick={() => setActive(s.slug)}
              >
                {s.navLabel}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <Reveal>
            <p className="kicker">Warum CCS</p>
            <h2 className="display" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", marginBottom: "1.5rem" }}>
              Verbindlich. Lokal. Aus einer Hand.
            </h2>
          </Reveal>
          <div className="reasons">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} className="reason" delay={i * 80}>
                <h3>{r.title}</h3>
                <p>{r.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <Reveal>
            <p className="kicker">Ablauf</p>
            <h2 className="display" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)", margin: "0.5rem 0 1.5rem" }}>
              Vier Schritte, dann Raum.
            </h2>
          </Reveal>
          <div className="process">
            {STEPS.map((s) => (
              <div key={s.n} className="process-step">
                <span className="process-num">{s.n}</span>
                <h3>{s.title}</h3>
                <p className="muted">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section teaser">
        <div className="wrap teaser-grid">
          <Reveal>
            <p className="kicker" style={{ color: "var(--cyan)" }}>
              Reinigung
            </p>
            <h2 className="display">Räume, die sich nach Arbeit anfühlen – nur ohne die Arbeit.</h2>
            <p>
              Unterhalt, Fenster, Treppenhaus oder Brandschaden: wir reinigen mit
              Fachpersonal, umweltfreundlichen Mitteln und einem Preis, der fest bleibt.
            </p>
            <ul>
              {CLEANING.points.slice(0, 4).map((p) => (
                <li key={p}>
                  <span aria-hidden="true">▸</span>
                  {p}
                </li>
              ))}
            </ul>
            <Link className="btn btn-primary" to="/reinigung">
              Reinigung ansehen
            </Link>
          </Reveal>
          <Reveal className="teaser-visual" delay={100}>
            <Picture
              src="/images/opt/cleaning-service-1.webp"
              fallback="/images/cleaning-service-1.jpg"
              alt="Team bei der Gewerbereinigung in einem Büro"
              width={1631}
              height={587}
            />
          </Reveal>
        </div>
      </section>

      <section className="section">
        <div className="wrap">
          <Reveal>
            <p className="kicker">Einsatzgebiet</p>
            <h2 className="display" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
              Nah am Objekt. Schnell vor Ort.
            </h2>
            <p className="muted" style={{ marginTop: "0.75rem", maxWidth: "36rem" }}>
              Sitz in Mülheim an der Ruhr – im Einsatz im gesamten westlichen Ruhrgebiet.
            </p>
            <div className="area-list">
              {SITE.area.map((city) => (
                <span className="chip" key={city}>
                  {city}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section" style={{ paddingTop: 0 }}>
        <div className="wrap">
          <Reveal>
            <p className="kicker">Fragen</p>
            <h2 className="display" style={{ fontSize: "clamp(2rem, 5vw, 3.2rem)" }}>
              Klarheit vor dem ersten Termin.
            </h2>
          </Reveal>
          <div className="faq">
            {FAQS.map((item) => (
              <details key={item.q}>
                <summary>{item.q}</summary>
                <p>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section cta-band">
        <div className="wrap">
          <p className="kicker" style={{ color: "var(--cyan)", justifyContent: "center" }}>
            Nächster Schritt
          </p>
          <h2 className="display">Bereit, Raum zu schaffen?</h2>
          <p>Schildern Sie kurz das Vorhaben. Wir melden uns mit einem klaren Vorschlag.</p>
          <div className="cta-actions">
            <Link className="btn btn-primary" to="/kontakt">
              Anfrage starten
            </Link>
            <a className="btn btn-ghost" href={`tel:${SITE.phoneTel}`}>
              {SITE.phoneDisplay}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
